// W37b (2026-10-20): session-scoped GET for the manager reads
// (GET /v1/team-live/overview, GET /v1/executive/summary).
//
// Why not useScopedData: that hook folds every 401 / 403 into one
// "outside your assigned scope" card and discards the error body. The
// manager routes answer 403 `MANAGER_ROLE_REQUIRED` with the session's
// role in the body (routes/stubs-w3c.mjs managerRoleDenied(), READ), and
// the screens want to say exactly that. Why not runtime.signed: it is
// null until the runtime base URL is injected, and a null runtime used to
// leave the screens on their design fixture (screen audit finding 1).
// This hook issues a relative fetch when no base URL is injected, with
// the same session headers as every other same-origin /v1 read
// (buildSessionHeaders: Authorization, X-Customer-Id, X-Portal-Id,
// X-User-Id / X-User-Email on url-bootstrap sessions, which is what the
// api-server resolves the role from).
//
// Status 'preview' means the caller should render its design fixture
// (dev mock session or ?preview=); nothing is fetched in that case.
import { useEffect, useRef, useState } from 'react';
import { isDevMockSession } from '../../../auth/mockContext.js';
import { useOptionalSession } from '../../../auth/session-context.js';
import { usePreviewFixtureMode } from '../../_shared/useAuthenticatedCustomer.js';
import { buildSessionHeaders } from '../../_shared/useContactProfile.js';
import { readRuntimeBaseUrl } from '../../CurrentActionReview/runtime-clients.js';

export type ManagerReadState<T> =
  | { status: 'preview' }
  | { status: 'pending' }
  | { status: 'loading' }
  | { status: 'ready'; body: T }
  | { status: 'forbidden'; errorCode: string; detail: string | null; role: string | null }
  | { status: 'unauthorized'; errorCode: string; detail: string | null }
  | { status: 'error'; httpStatus: number; errorCode: string; detail: string | null };

export interface UseManagerReadArgs {
  path: string;
  /** False skips the fetch (test harness state overrides). */
  enabled?: boolean;
  fetchImpl?: typeof fetch;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function useManagerRead<T>({ path, enabled = true, fetchImpl }: UseManagerReadArgs): ManagerReadState<T> {
  const sessionCtx = useOptionalSession();
  const previewFlag = usePreviewFixtureMode();
  const devMock = isDevMockSession(sessionCtx?.session ?? null);
  const preview = previewFlag || devMock;
  const session = sessionCtx?.session ?? null;
  const sessionStatus = sessionCtx?.status ?? null;
  const sessionIdentity = session ? `${session.portalId}:${session.userId}:${session.token}` : null;

  // Preview is known synchronously, so the fixture renders on the first
  // paint instead of behind a one-frame loading card.
  const [state, setState] = useState<ManagerReadState<T>>(() =>
    preview ? { status: 'preview' } : { status: 'pending' },
  );
  const depsRef = useRef<{ fetchImpl: typeof fetch | null; baseUrl: string }>({
    fetchImpl: fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null),
    baseUrl: readRuntimeBaseUrl(),
  });

  useEffect(() => {
    if (preview) {
      setState({ status: 'preview' });
      return;
    }
    if (!enabled) {
      setState({ status: 'pending' });
      return;
    }
    if (!sessionCtx || sessionStatus === 'pending') {
      setState({ status: 'pending' });
      return;
    }
    if (sessionStatus === 'error' || sessionStatus === 'expired' || !session) {
      setState({ status: 'unauthorized', errorCode: 'SESSION_UNAVAILABLE', detail: null });
      return;
    }
    const impl = depsRef.current.fetchImpl;
    if (!impl) {
      setState({ status: 'error', httpStatus: 0, errorCode: 'NO_FETCH', detail: 'No fetch implementation is available.' });
      return;
    }
    const base = depsRef.current.baseUrl.endsWith('/')
      ? depsRef.current.baseUrl.slice(0, -1)
      : depsRef.current.baseUrl;
    let cancelled = false;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const response = await impl(`${base}${path}`, { method: 'GET', headers: buildSessionHeaders(session) });
        if (cancelled) return;
        let body: unknown = null;
        try {
          body = await response.json();
        } catch {
          body = null;
        }
        if (cancelled) return;
        const errorCode = isRecord(body) ? readString(body.errorCode) : null;
        const detail = isRecord(body) ? readString(body.detail) : null;
        if (response.status === 403) {
          setState({
            status: 'forbidden',
            errorCode: errorCode ?? 'FORBIDDEN',
            detail,
            role: isRecord(body) ? readString(body.role) : null,
          });
          return;
        }
        if (response.status === 401) {
          setState({ status: 'unauthorized', errorCode: errorCode ?? 'UNAUTHORIZED', detail });
          return;
        }
        if (!response.ok) {
          setState({ status: 'error', httpStatus: response.status, errorCode: errorCode ?? `HTTP_${response.status}`, detail });
          return;
        }
        setState({ status: 'ready', body: body as T });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          httpStatus: 0,
          errorCode: 'NETWORK_ERROR',
          detail: err instanceof Error ? err.message : 'Network error while loading data.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // sessionIdentity stands in for the session object so a handshake
    // re-emit with the same identity does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, enabled, path, sessionStatus, sessionIdentity]);

  return state;
}
